import { HttpClient, HttpHandler, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ConvertHelper, DotNetBridge } from '@barthauer/abc/core';
import { CompressionService } from '@barthauer/abc/core/assets-manager';
import {
  AuthConfigService,
  ISupportsRequestHeaderValues,
  ProfileSettingsService,
} from '@barthauer/abc/core/authentication';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import { ErrorHandlingService } from '@barthauer/abc/core/error-handling';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { LoginComponentInfo } from 'libs/abc/core/authentication/src/lib/login/login-component-info';
import { WebApiEditorComponentInfo } from 'libs/abc/core/authentication/src/lib/webapi-editor/webapi-editor-component-info';
import { forkJoin, from, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, map, tap } from 'rxjs/operators';
import { AbcCoreDataModule } from '../abc-core-data.module';
import { CacheStorageAdapter, CacheStorageDataSet } from './cache-storage';
import { CacheContext, CacheDataSet, CacheProgress } from './cache.model';

@Injectable({
  providedIn: AbcCoreDataModule,
})
export class CacheService {
  public progressRatio: number;
  public errorRatio: number;
  public progressState: ProgressState;
  public lastErrorMessage: string;
  public cacheInfoText: string;
  public storage: CacheStorageAdapter;
  private isHttpCacheAvailable: boolean;
  private defaultClientPingTimeoutMilliseconds_: number;

  private static instance: CacheService;
  private httpCacheDataSet: CacheDataSet;
  private httpCacheStorageDataSet: CacheStorageDataSet;
  private supportsGzipRequestContentEncoding: boolean;

  private cachingSubject: Subject<CacheContext> = new Subject<CacheContext>();
  private progressChangedSubject: Subject<void> = new Subject<void>();
  private resAsync: Promise<ResourceSet>;

  public static isCachingDisabled: boolean;
  public static isRequestCompressionDisabled: boolean;

  private get CLIENT_PING_TIMEOUT_IN_MS(): string {
    return 'client_ping_timeout_in_ms';
  }

  private get CLIENT_MAX_ZOOM_LEVEL(): string {
    return 'client_max_zoom_level';
  }

  private get CLIENT_CACHED_ZONE_OPTION(): string {
    return 'client_cached_zone_option';
  }

  public get defaultClientPingTimeoutMilliseconds(): number {
    return this.defaultClientPingTimeoutMilliseconds_;
  }

  constructor(
    authConfigService: AuthConfigService,
    private profileSettingsService: ProfileSettingsService,
    configService: AbcConfigService,
    httphandler: HttpHandler,
    resourceService: ResourceManagerService,
    public error: ErrorHandlingService,
    private compression: CompressionService,
    router: Router
  ) {
    this.resAsync = resourceService.getResourceSet('abc-core-data-caching');
    this.progressState = ProgressState.Idle;
    const http = new HttpClient(httphandler);
    this.storage = new CacheStorageAdapter(
      () => this.profileSettingsService.buildProfileKey().toPromise(),
      () => {
        if (CacheStorageAdapter.isDownloadComplete && CacheStorageAdapter.forceOfflineMode) {
          return of({ available: true, timeout: true }).toPromise();
        } else if (
          !LoginComponentInfo.isCurrentPageLoginPage(router) &&
          !WebApiEditorComponentInfo.isCurrentPageWebApiEditorPage(router) &&
          (CacheStorageAdapter.isDownloadComplete || configService.isAppLike !== false)
        ) {
          const start = Date.now();
          return (
            http
              .get(configService.configuration.WebApi.BaseURL + '/Ping')
              //.get('https://httpbin.org/get')
              .pipe(
                concatMap((_) => authConfigService.getCurrent(true)),
                map((_) => {
                  if (_) {
                    this.defaultClientPingTimeoutMilliseconds_ = _.clientPingTimeoutMilliseconds;
                    const milliseconds: number = Date.now() - start;
                    const clientPingTimeout = this.getClientOfflineTimeout() ?? _.clientPingTimeoutMilliseconds;
                    return { available: true, timeout: milliseconds > (clientPingTimeout ?? 500) };
                  } else {
                    return { available: false, timeout: true };
                  }
                }),
                catchError((_) => {
                  return of({ available: false, timeout: true });
                })
              )
              .toPromise()
          );
        } else {
          return of({ available: true, timeout: false }).toPromise();
        }
      },
      this.error
    );
    authConfigService.current.subscribe((_) => {
      if (_) {
        this.storage.setDefaultWaitForOnlineModeIntervalSeconds(
          _.clientPingIntervalMinutes ? _.clientPingIntervalMinutes * 60 : undefined
        );
        this.supportsGzipRequestContentEncoding = _.requestContentEncoding?.indexOf('gzip') >= 0;
      }
    });
    this.httpCacheDataSet = new CacheDataSet('GenericHttpCache');
    this.storage.items.push(this.httpCacheDataSet);

    CacheService.instance = this;

    let weights: number[] = [];
    let ratios: number[] = [];
    let errorRatios: number[] = [];
    let weightSum = 0.0;
    let oldProgressRatio = 0.0;

    this.cachingSubject
      .asObservable()
      .pipe(
        map((ctx) => {
          CacheStorageAdapter.notifyDownloadStarted();
          let items: CacheDataSet[] = this.storage.getCacheableItems();
          weights = [];
          ratios = [];
          errorRatios = [];
          weightSum = 0;
          oldProgressRatio = 0;
          for (let i = 0; i < items.length; i++) {
            const step = items[i];
            const weight = step.getWeight();
            weights.push(weight);
            ratios.push(0.0);
            errorRatios.push(0.0);
            weightSum += weight;
          }
          return { ctx: ctx, items: items };
        })
      )
      .pipe(
        concatMap((_) => {
          const ctx = _.ctx;
          const items = _.items;
          this.progressState = ProgressState.Caching;
          this.progressChangedSubject.next();
          ctx.isCancelled = false;
          const observables: Observable<CacheProgress>[] = new Array<Observable<CacheProgress>>();
          for (let k = 0; k < items.length; k++) {
            const i = k;
            const step = items[i];
            let executeStep = step.cacheMethod(ctx).pipe(
              tap((_) => {
                if (this.progressState === ProgressState.Cancelling) {
                  ctx.isCancelled = true;
                } else {
                  const step = _.stepCurrentValue + _.errorCurrentValue;
                  if (step >= 1.0 || ratios[i] >= 1.0) {
                    //"final" call per step
                    ratios[i] = 1.0;
                    if (_.errorCurrentValue) {
                      errorRatios[i] = _.errorCurrentValue;
                    }
                  } else {
                    if (isNaN(step) || step === undefined || step === null) ratios[i] = 1.0;
                    else ratios[i] = step;
                    if (_.errorCurrentValue) {
                      errorRatios[i] = _.errorCurrentValue;
                    }
                  }
                  let sum = 0.0;
                  let sumError = 0.0;

                  for (let j = 0; j < items.length; j++) {
                    sum += ratios[j] * weights[j];
                    sumError += (errorRatios[j] ?? 0.0) * weights[j];
                  }
                  this.progressRatio = (sum + sumError) / weightSum;
                  this.errorRatio = sumError / weightSum;
                  if (_.errorMessage) {
                    this.lastErrorMessage = _.errorMessage.trim();
                  }
                  if (this.progressRatio >= oldProgressRatio + 0.05) {
                    oldProgressRatio = this.progressRatio;
                    this.estimateCacheUtilization();
                  }
                  this.progressChangedSubject.next();
                }
              })
            );
            observables.push(executeStep);
          }
          return forkJoin(observables);
        })
      )
      .subscribe(
        (_) => {
          if (this.progressState === ProgressState.Cancelling) {
            this.resetProgress();
          } else {
            this.progressRatio = 1.0;
          }
          this.progressState = ProgressState.Idle;
          this.progressChangedSubject.next();
          this.estimateCacheUtilization(10000);
          CacheStorageAdapter.notifyDownloadComplete();
        },
        (err) => {
          this.progressState = ProgressState.Idle;
          this.progressChangedSubject.next();
          this.estimateCacheUtilization(10000);
          CacheStorageAdapter.notifyDownloadCancelled();
        }
      );

    this.resetProgress();

    this.estimateCacheUtilization();
  }

  public getClientOfflineTimeout(): number {
    const clientValue = localStorage.getItem(this.CLIENT_PING_TIMEOUT_IN_MS) ?? '';
    const val = parseInt(clientValue);
    if (!isNaN(val) && val > 0) return val;
    else return null;
  }

  public setClientOfflineTimeout(milliseconds: number) {
    if (milliseconds !== undefined && milliseconds !== null) {
      localStorage.setItem(this.CLIENT_PING_TIMEOUT_IN_MS, milliseconds.toString());
    } else {
      localStorage.removeItem(this.CLIENT_PING_TIMEOUT_IN_MS);
    }
  }

  public getClientMaxZoomLevel(): number {
    const clientValue = localStorage.getItem(this.CLIENT_MAX_ZOOM_LEVEL) ?? '';
    const val = parseInt(clientValue);
    if (!isNaN(val) && val > 0) return val;
    else return null;
  }

  public setClientMaxZoomLevel(level: number) {
    if (level !== undefined && level !== null) {
      localStorage.setItem(this.CLIENT_MAX_ZOOM_LEVEL, level.toString());
    } else {
      localStorage.removeItem(this.CLIENT_MAX_ZOOM_LEVEL);
    }
  }

  public getClientCachedZoneOption(): number {
    const clientValue = localStorage.getItem(this.CLIENT_CACHED_ZONE_OPTION) ?? '';
    const val = parseInt(clientValue);
    if (!isNaN(val) && val > 0) return val;
    else return null;
  }

  public setClientCachedZoneOption(optionVal: number) {
    if (optionVal !== undefined && optionVal !== null) {
      localStorage.setItem(this.CLIENT_CACHED_ZONE_OPTION, optionVal.toString());
    } else {
      localStorage.removeItem(this.CLIENT_CACHED_ZONE_OPTION);
    }
  }

  public onProgressChanged(): Observable<void> {
    return this.progressChangedSubject.asObservable();
  }

  public resetProgress(): void {
    this.progressRatio = 0;
    this.errorRatio = 0;
    this.lastErrorMessage = null;
  }

  public get isOnline(): boolean {
    return CacheStorageAdapter.isOnline;
  }

  public get forceOfflineMode(): boolean {
    return CacheStorageAdapter.forceOfflineMode;
  }

  public get useOfflineMode(): boolean {
    return CacheStorageAdapter.useOfflineMode;
  }

  public get isServerAvailable(): boolean {
    return CacheStorageAdapter.isServerAvailable;
  }

  public get isStorageAvailable(): boolean {
    return this.isHttpCacheAvailable;
  }

  private async getHttpCacheAsync(): Promise<CacheStorageDataSet> {
    let ds = this.httpCacheStorageDataSet;
    if (!(await this.storage.isInCurrentContext(ds))) {
      ds = await this.storage.ensureStorageDataSet(this.httpCacheDataSet);
      this.httpCacheStorageDataSet = ds;
    }
    this.isHttpCacheAvailable = ds ? true : false;
    return ds;
  }

  public async estimateCacheUtilization(delayMilliseconds: number = 0.0): Promise<void> {
    const estimate = await this.storage.estimateCacheUtilization();
    await this.updateCacheInfoText(estimate?.used, estimate?.quota);

    if (delayMilliseconds > 0.0) {
      setTimeout(() => {
        this.estimateCacheUtilization();
      }, delayMilliseconds);
    }
  }

  public async clearCache() {
    this.httpCacheStorageDataSet = null;
    await this.storage.clear();
    await this.estimateCacheUtilization(10000);
  }

  private async updateCacheInfoText(used?: number, quota?: number): Promise<void> {
    let text: string;
    if (quota) {
      text = (await this.resAsync).get(
        'CacheInfoTextQuota',
        ((used ?? 0.0) * 100).toFixed(2),
        ConvertHelper.toHumanByteString(quota, true)
      );
    } else {
      //https://stackoverflow.com/questions/7944460/detect-safari-browser
      const isSafari: boolean = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (isSafari) {
        text = (await this.resAsync).get('CacheInfoTextSafariNotSupported');
      } else {
        const isHttps: boolean = document.location.protocol === 'https:';
        const isLocalhost: boolean = document.location.hostname === 'localhost';
        if (!isHttps && !isLocalhost) {
          text = (await this.resAsync).get('CacheInfoTextRequiresHttps');
        } else {
          text = (await this.resAsync).get('CacheInfoTextUnknownMemory');
        }
      }
    }
    this.cacheInfoText = text;
  }

  public startCachingAsync(ctx: CacheContext) {
    this.resetProgress();
    this.cachingSubject.next(ctx);
  }
  public stopCaching() {
    this.progressState = ProgressState.Cancelling;
    this.progressChangedSubject.next();
  }

  public httpPost(
    http: HttpClient,
    url: string,
    body: any | null,
    options: {
      headers?:
        | HttpHeaders
        | {
            [header: string]: string | string[];
          };
      observe?: 'body';
      params?:
        | HttpParams
        | {
            [param: string]: string | string[];
          };
      reportProgress?: boolean;
      responseType: 'arraybuffer';
      withCredentials?: boolean;
    },
    noCache?: boolean,
    refreshCache?: boolean
  ): Observable<ArrayBuffer> {
    return this.executeRequest(url, body, options, noCache, refreshCache, (body_) =>
      CacheService.httpPostCore(http, url, body_, options)
    );
  }

  public httpPostBlob(
    http: HttpClient,
    url: string,
    body: any | null,
    options: {
      headers?:
        | HttpHeaders
        | {
            [header: string]: string | string[];
          };
      observe?: 'body';
      params?:
        | HttpParams
        | {
            [param: string]: string | string[];
          };
      reportProgress?: boolean;
      responseType: 'blob';
      withCredentials?: boolean;
    },
    noCache?: boolean,
    refreshCache?: boolean
  ): Observable<Blob> {
    return this.executeRequest(url, body, options, noCache, refreshCache, (body_) => http.post(url, body_, options));
  }

  public httpPostObject(
    http: HttpClient,
    url: string,
    body: any | null,
    options?: {
      headers?:
        | HttpHeaders
        | {
            [header: string]: string | string[];
          };
      observe?: 'body';
      params?:
        | HttpParams
        | {
            [param: string]: string | string[];
          };
      reportProgress?: boolean;
      responseType?: 'json';
      withCredentials?: boolean;
    },
    noCache?: boolean,
    refreshCache?: boolean
  ): Observable<Object> {
    return this.executeRequest(url, body, options, noCache, refreshCache, (body_) => http.post(url, body_, options));
  }

  public httpPostText(
    http: HttpClient,
    url: string,
    body: any | null,
    options?: {
      headers?:
        | HttpHeaders
        | {
            [header: string]: string | string[];
          };
      observe?: 'body';
      params?:
        | HttpParams
        | {
            [param: string]: string | string[];
          };
      reportProgress?: boolean;
      responseType: 'text';
      withCredentials?: boolean;
    },
    noCache?: boolean,
    refreshCache?: boolean
  ): Observable<string> {
    return this.executeRequest(url, body, options, noCache, refreshCache, (body_) => http.post(url, body_, options));
  }

  public httpGetObject(http: HttpClient, url: string, noCache?: boolean, refreshCache?: boolean): Observable<Object> {
    return this.executeRequest(url, null, null, noCache, refreshCache, (body_) => http.get(url));
  }

  public static httpPostStatic(
    http: HttpClient,
    url: string,
    body: any | null,
    options: {
      headers?:
        | HttpHeaders
        | {
            [header: string]: string | string[];
          };
      observe?: 'body';
      params?:
        | HttpParams
        | {
            [param: string]: string | string[];
          };
      reportProgress?: boolean;
      responseType: 'arraybuffer';
      withCredentials?: boolean;
    },
    noCache?: boolean,
    refreshCache?: boolean,
    isStatic?: boolean
  ): Observable<ArrayBuffer> {
    if (this.instance && !isStatic) {
      return this.instance.httpPost(http, url, body, options, noCache, refreshCache);
    }
    return this.httpPostCore(http, url, body, options);
  }

  static httpPostCore(
    http: HttpClient,
    url: string,
    body: any | null,
    options: {
      headers?:
        | HttpHeaders
        | {
            [header: string]: string | string[];
          };
      observe?: 'body';
      params?:
        | HttpParams
        | {
            [param: string]: string | string[];
          };
      reportProgress?: boolean;
      responseType: 'arraybuffer';
      withCredentials?: boolean;
    }
  ): Observable<ArrayBuffer> {
    return http.post(url, body, options);
  }
  private executeRequest<T>(
    url: string,
    body: any | null,
    options: any,
    noCache: boolean,
    refreshCache: boolean,
    action: (body) => Observable<T>
  ): Observable<T> {
    return from(this.executeRequestAsync(url, body, options, noCache, refreshCache, action));
  }

  private async executeRequestAsync<T>(
    url: string,
    body: any | null,
    options: any,
    noCache: boolean,
    refreshCache: boolean,
    action: (body) => Observable<T>
  ): Promise<T> {
    let key: string;

    if (!noCache) {
      if (CacheService.isCachingDisabled) {
        noCache = true;
      } else if (DotNetBridge.hasContext) {
        noCache = true;
      } else {
        key = url;
        if (body && body !== '') {
          key += '?' + JSON.stringify(body);
        }
      }
    }

    //A4 (150 DPI) Report = > 1000000
    if (noCache === undefined && key.length > 500000) {
      noCache = true;
    }

    if (
      noCache &&
      !CacheService.isRequestCompressionDisabled &&
      this.supportsGzipRequestContentEncoding &&
      options &&
      ((options.headers && options.headers['Content-Type'] === 'application/json') ||
        (typeof body === 'object' &&
          !(body instanceof Blob) &&
          !(body as ISupportsRequestHeaderValues)?.getRequestHeaderValue) ||
        typeof body === 'string')
    ) {
      try {
        body = await this.compression.gzipAsArrayBuffer(body);
        if (!(options.headers instanceof HttpHeaders)) {
          options.headers = new HttpHeaders(options.headers);
        }
        options.headers = options.headers.append('Content-Encoding', 'gzip');
      } catch (err) {
        console.error(err);
      }
    }

    let result: T;
    if (noCache || refreshCache) {
      result = await action(body).toPromise();
      if (!noCache) {
        await this.httpCacheStorageDataSet?.set(key, result);
      }
    } else {
      const cache = await this.getHttpCacheAsync();

      if (!CacheStorageAdapter.isOnline) {
        result = await this.getResponseFromCacheAsync<T>(cache, key, false);
      }
      if (!result) {
        try {
          result = await action(body).toPromise();
          await this.httpCacheStorageDataSet?.set(key, result);
        } catch (err) {
          result = await this.getResponseFromCacheAsync<T>(cache, key, true);
          if (!result) {
            throw err;
          }
        }
      }
    }
    return result;
  }

  private async getResponseFromCacheAsync<T>(
    dataSet: CacheStorageDataSet,
    key: string,
    allowNull: boolean
  ): Promise<T> {
    const value = await dataSet?.get(key);
    if (allowNull || value) {
      return value;
    } else if (typeof value == 'object') {
      if (Array.isArray(value)) {
        return [] as any as T;
      } else {
        return {} as any as T;
      }
    } else {
      return value;
    }
  }

  public getMaxZoom(): number {
    let zoom: number = 0;
    let items: CacheDataSet[] = this.storage.getCacheableItems();
    for (let i of items) {
      if (i.getInfo) {
        const info = i.getInfo();
        if (info && info.maxZoom > zoom) {
          zoom = info.maxZoom;
        }
      }
    }
    return zoom;
  }

  public async isDataSetAvailableForContext(
    datasetName: string,
    contextKey: string,
    dataTableName?: string,
    dataKey?: string
  ): Promise<boolean> {
    const storage = new CacheStorageAdapter(
      () => Promise.resolve(contextKey),
      () => Promise.resolve({ available: CacheStorageAdapter.isServerAvailable, timeout: false }),
      this.error
    );
    for (const item of this.storage.items) {
      if (item.name === datasetName) {
        const ds = await storage.getStorageDataSet(item);
        if (ds && (await storage.isInContext(ds, contextKey))) {
          if (!dataTableName || !dataKey || (await ds.get(dataKey, dataTableName))) {
            return true;
          }
        }
      }
    }
    return false;
  }
}

export enum ProgressState {
  Caching = 1,
  Cancelling,
  Idle,
}
